import { type QueuedModal } from '@/app-shell/modal-queue.types';
import { isNativeDesktopE2eLane } from '@/features/flags-dev/desktop-e2e-lane';
import { resolveTierGateContext } from '@/lib/modalTiers';
import { ModalId } from '@heidi/schema/ml-scribe';
import { atom } from 'jotai';
import { appStateForModalsAtom, launchModalsAtom } from './launchModalsConfigAtom';
import {
  educationJourneyModalIdAtom,
  isWithheldByEducationMutex,
  recordEducationJourneyAtom,
} from './modalJourneyObservation';
import {
  devPacingExemptModalIdsAtom,
  isModalPacingEnabledAtom,
  isPacedModal,
  pacedModalSeenCountAtom,
  PACING_MAX_PER_SESSION,
} from './modalPacing';
import {
  isModalDisplayAllowedByTier,
  isModalTierFrameworkEnabledAtom,
} from './modalTierFramework';

/**
 * Launch popups the native desktop e2e lane still lets through the queue.
 *
 * The native lane drives the app through the OS accessibility tree, so it can
 * neither set `__SUPPRESS_GLOBAL_MODALS__` nor intercept requests. Any new
 * auto-shown popup, mounted inside `GlobalModals` or not, would cover the app
 * on its first run. So in that lane the queue admits no launch popup unless
 * it is named here. A native test that exercises a popup adds its id.
 * User-triggered popups are always admitted: a test press asked for them.
 */
export const NATIVE_E2E_ADMITTED_LAUNCH_MODAL_IDS: ReadonlySet<ModalId> =
  new Set<ModalId>([
    // A real failure state, not a promotion: hiding it would turn a blocked
    // network into an unexplained timeout.
    ModalId.T1_NETWORK_FIREWALL_ERROR_MODAL,
  ]);

/** Whether the queue may show this launch popup on this page. */
export const isLaunchModalAdmittedInE2e = (
  modalId: ModalId,
  nativeE2ePage: boolean = isNativeDesktopE2eLane()
): boolean => !nativeE2ePage || NATIVE_E2E_ADMITTED_LAUNCH_MODAL_IDS.has(modalId);

/**
 * Set of modal IDs that have been seen by the user
 * Synced by useQueryUser when user data is fetched from backend
 * Updated by useQueuedModal when modals are closed (optimistic update)
 *
 * Initialized as null to prevent race condition where modals briefly flash
 * before backend data loads. The null check in launchModalsAtom guards against this.
 */
export const seenModalIdsAtom = atom<Set<ModalId> | null>(null);

/**
 * User-triggered modals
 * These are modals triggered by user actions (button clicks, etc.)
 */
export const userTriggeredModalsAtom = atom<QueuedModal[]>([]);

export { launchModalsAtom };

/**
 * Launch popups the queue may show on this page: every launch popup, except
 * in the native desktop e2e lane, where only the allowlisted ones.
 */
const admittedLaunchModalsAtom = atom<QueuedModal[]>((get) => {
  const launchModals = get(launchModalsAtom);
  // Read once per evaluation, not once per popup.
  if (!isNativeDesktopE2eLane()) return launchModals;
  return launchModals.filter((modal) => isLaunchModalAdmittedInE2e(modal.id, true));
});

/**
 * Priority-sorted modal queue combining launch and user-triggered modals
 * Launch modals are already filtered by seen status in launchModalsConfigAtom.
 * Includes popups the native desktop e2e lane withholds; queue consumers that
 * mean "something will show" read `admittedModalQueueAtom` instead.
 */
export const modalQueueAtom = atom<QueuedModal[]>((get) => {
  const launchModals = get(launchModalsAtom);
  const userModals = get(userTriggeredModalsAtom);
  return [...launchModals, ...userModals].sort((a, b) => b.priority - a.priority);
});

/**
 * `modalQueueAtom` minus launch popups the native desktop e2e lane withholds,
 * which would otherwise sit in the queue for the whole run. Identical to
 * `modalQueueAtom` outside that lane. Not filtered by tier or pacing.
 */
export const admittedModalQueueAtom = atom<QueuedModal[]>((get) => {
  const launchModals = get(admittedLaunchModalsAtom);
  const userModals = get(userTriggeredModalsAtom);
  return [...launchModals, ...userModals].sort((a, b) => b.priority - a.priority);
});

/**
 * Quiet period + active recording + session cap.
 * Exempt / user-triggered ignore this. Recording uses `appState.isRecording`
 * (timer running) so paced popups can't interrupt a consult.
 */
export const pacedModalsAllowedAtom = atom<boolean>((get) => {
  const appState = get(appStateForModalsAtom);
  if (!appState.isUserAtLeastAWeekOld) return false;
  if (appState.isRecording) return false;
  if (get(pacedModalSeenCountAtom) >= PACING_MAX_PER_SESSION) return false;
  return true;
});

/**
 * Highest-priority modal to show. Three flag-gated filters run over launch
 * surfaces before the merge+sort; user-triggered modals bypass all three.
 *  - Tier gate: withhold a launch surface whose assigned tier denies
 *    display now. Unmapped surfaces pass through untouched (rollout is partial).
 *  - R5 mutex (tier framework flag): once one T4/T5 journey has a confirmed
 *    display this page load (`educationJourneyModalIdAtom`), withhold every
 *    other T4/T5 launch surface. Being the head alone confirms nothing.
 *  - Pacing: withhold paced launch surfaces while the pacing levers say so.
 * Reads `admittedLaunchModalsAtom`, so the native desktop e2e lane also
 * withholds any launch surface not on `NATIVE_E2E_ADMITTED_LAUNCH_MODAL_IDS`.
 * Both flags OFF → behaves exactly like `admittedModalQueueAtom.at(0)`.
 */
export const currentQueuedModalAtom = atom<QueuedModal | null>((get) => {
  const userModals = get(userTriggeredModalsAtom);

  const tierFrameworkEnabled = get(isModalTierFrameworkEnabledAtom);
  const appState = get(appStateForModalsAtom);
  const tierCtx = resolveTierGateContext({
    isClinicalRecordingActive: appState.isClinicalRecordingActive,
    isUserManaged: appState.isUserManaged,
    isUserAtLeastAWeekOld: appState.isUserAtLeastAWeekOld,
  });
  const passesTierGate = (modal: QueuedModal) =>
    !tierFrameworkEnabled || isModalDisplayAllowedByTier(modal.id, tierCtx);

  const pacingWithholding = get(isModalPacingEnabledAtom) && !get(pacedModalsAllowedAtom);
  const devExempt = get(devPacingExemptModalIdsAtom);
  const passesPacing = (modal: QueuedModal) =>
    !pacingWithholding || !isPacedModal(modal.id) || devExempt.includes(modal.id);

  // R5: the journey already shown stays eligible so it can finish.
  const journeyId = get(educationJourneyModalIdAtom);
  const passesEducationMutex = (modal: QueuedModal) =>
    !isWithheldByEducationMutex(tierFrameworkEnabled, journeyId, modal.id);

  const launchModals = get(admittedLaunchModalsAtom).filter(
    (modal) => passesTierGate(modal) && passesEducationMutex(modal) && passesPacing(modal)
  );
  return (
    [...launchModals, ...userModals].sort((a, b) => b.priority - a.priority).at(0) ?? null
  );
});

/**
 * Consume a pacing slot for a dismissed paced launch modal.
 * Explicit user-triggered (Force show, banner click) does not count.
 * The pacing counter is a no-op unless pacing is on and this is the queue head.
 * Separately confirms a closing T4/T5 launch head as the R5 journey, flags or
 * not: a queued close means the surface displayed.
 * Call BEFORE seen-state write / dequeue (else launch list drops it and guard fails).
 * Prefer `useQueuedModal.handleClose`. Firmware is the exception: it records on show.
 */
export const recordPacedLaunchModalDismissedAtom = atom(
  null,
  (get, set, modalId: ModalId) => {
    const current = get(currentQueuedModalAtom);
    // Record even with pacing OFF; only the tier framework's R5 gate reads it.
    if (
      current?.id === modalId &&
      get(launchModalsAtom).some((modal) => modal.id === modalId)
    ) {
      set(recordEducationJourneyAtom, modalId);
    }
    if (!get(isModalPacingEnabledAtom)) return;
    if (!current || current.id !== modalId || !isPacedModal(modalId)) return;
    if (get(devPacingExemptModalIdsAtom).includes(modalId)) return;
    if (!get(launchModalsAtom).some((modal) => modal.id === modalId)) return;
    set(pacedModalSeenCountAtom, (count) => count + 1);
  }
);

/**
 * Write-only atom to enqueue a user-triggered modal
 * Handles deduplication internally — safe to call multiple times with the same modal ID
 */
export const enqueueModalAtom = atom(null, (_get, set, modal: QueuedModal) => {
  set(userTriggeredModalsAtom, (prev) => {
    if (prev.some((m) => m.id === modal.id)) return prev;
    return [...prev, modal];
  });
});

/**
 * Write-only atom to dequeue a user-triggered modal by ID
 */
export const dequeueModalAtom = atom(null, (_get, set, modalId: ModalId) => {
  set(userTriggeredModalsAtom, (prev) => prev.filter((m) => m.id !== modalId));
});
